/**
 * UTM attribution capture for the web app (Spec 3 of 3).
 *
 * The shared first-party cookie `ow_utm` (Domain=orderwerks.com) is the single
 * client-side store for marketing attribution. This module:
 *   - captures `utm_*` params off the URL on arrival and writes them to the cookie
 *     (last-touch, 30-day window), and
 *   - reads the cookie back at order-creation time so callers can attach it to the
 *     order-create payload under the `utm` key.
 *
 * No external cookie library is used (consistent with UserService.ts). The domain
 * resolution mirrors UserService.eraseCookie so localhost / LAN / prod all behave.
 */

const COOKIE_NAME = "ow_utm";
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days

/** Mirrors the domain logic in UserService.eraseCookie (localhost / 192.168.* / .orderwerks.com). */
function resolveCookieDomain(): string {
    const host = window.location.hostname;

    if (host.indexOf("192.168") >= 0) {
        return host;
    } else if (host !== "localhost") {
        return ".orderwerks.com";
    }

    return "localhost";
}

/**
 * The app reads query params off both the hash and the search string elsewhere
 * (see App.tsx loadUser). Mirror that: prefer a hash-embedded query, fall back to search.
 */
function readQueryParams(): URLSearchParams {
    const hash = window.location.hash;
    const fromHash = hash.includes("?") ? hash.split("?")[1] : "";

    return new URLSearchParams(fromHash || window.location.search);
}

/** Same scan as UserService.getCookie. */
function getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");

    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }

    return null;
}

/**
 * On page load: if one or more `utm_*` params are present in the URL, overwrite the
 * `ow_utm` cookie with the full utm_* set (last-touch, resetting the 30-day window).
 * No utm_* params -> leave the cookie untouched.
 */
export function captureUtmOnArrival(): void {
    const params = readQueryParams();
    const utm: Record<string, string> = {};

    params.forEach((value, key) => {
        if (key.toLowerCase().startsWith("utm_")) {
            utm[key] = value;
        }
    });

    if (Object.keys(utm).length === 0) {
        return; // no utm_* params -> do nothing
    }

    const domain = resolveCookieDomain();

    // Add Secure on HTTPS (production) so the attribution cookie isn't sent over plaintext;
    // omitted on http (localhost dev) where a Secure cookie would never be stored.
    const secure = window.location.protocol === "https:" ? "; Secure" : "";

    document.cookie =
        `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(utm))}; ` +
        `Max-Age=${MAX_AGE_SECONDS}; Path=/; Domain=${domain}; SameSite=Lax${secure}`;
}

/**
 * At order-creation time: parse the `ow_utm` cookie into an attribution map, or
 * `undefined` if the cookie is absent/empty/invalid. Cookie present implies within
 * the 30-day window by definition (Max-Age enforces it).
 */
export function getCapturedUtm(): Record<string, string> | undefined {
    const raw = getCookie(COOKIE_NAME);

    if (!raw) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(decodeURIComponent(raw));

        // Conform to the Record<string, string> contract: reject non-objects, arrays,
        // empty maps, and any map containing non-string values (tampered/foreign cookie).
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return undefined;
        }

        const entries = Object.entries(parsed);

        if (entries.length === 0 || entries.some(([, value]) => typeof value !== "string")) {
            return undefined;
        }

        return parsed as Record<string, string>;
    } catch {
        return undefined;
    }
}
